import type { JSX } from 'react';
import {
  SSH_AUTH_METHODS,
  SSH_AUTH_METHOD_KEYS,
  SSH_TERMS,
  type NetworkNode,
  type NodeSsh,
  type SshAuthMethod,
  type SshTerm,
} from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { CollapsibleSection } from '../../shared/ui/CollapsibleSection';
import { formatMoment } from '../../shared/format/time';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { NodeCredentials } from '../vnc/NodeCredentials';
import { canHostSsh } from './access';
import { useNodeSsh, useProbeSsh, useUpdateNodeSsh } from './api';

/** Что сказать, когда учётных данных для SSH ещё не завели. */
const NO_SSH_CREDENTIALS = 'credentials.noSshHint';

/**
 * SSH в панели узла.
 *
 * Здесь всё, что относится к терминалу именно на этой машине: кнопка, порт,
 * имя пользователя, способ входа, ключ хоста и результат зондирования.
 * Раздельные экраны для этого не годятся — инженер, у которого не входит
 * одна железка, должен видеть про неё всё в одном месте.
 */
export function SshSection({
  node,
  open,
  onToggle,
  canConnect,
  canWrite,
  canManageCredentials,
  onConnect,
}: {
  node: NetworkNode;
  /** Раскрыт ли раздел. Решает это панель: раскрытым может быть только один. */
  open: boolean;
  onToggle: () => void;
  canConnect: boolean;
  canWrite: boolean;
  canManageCredentials: boolean;
  onConnect: (node: { id: string; name: string }) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const settings = useNodeSsh(node.id);
  const update = useUpdateNodeSsh(node.id);
  const probe = useProbeSsh(node.id);

  // Типы, у которых командной строки не бывает, и всё, у чего нет адреса,
  // подключать некуда — раздела SSH у таких узлов нет вовсе.
  if (!canHostSsh(node.kind) || (node.address ?? '').trim().length === 0) {
    return null;
  }

  const ssh = settings.data;

  return (
    <CollapsibleSection
      title="SSH"
      icon="navSsh"
      open={open}
      onToggle={onToggle}
      actions={
        <Button
          size="sm"
          variant="primary"
          icon="navSsh"
          disabled={!canConnect}
          title={canConnect ? undefined : t('map.menu.noSshPermission')}
          onClick={() => onConnect({ id: node.id, name: node.name })}
        >
          {t('nav.ssh')}
        </Button>
      }
    >
      {ssh ? (
        <>
          {/* Итог зондирования внутри раздела, а не под заголовком: строка,
              видная у свёрнутого, удваивала бы высоту всего свёрнутого. */}
          <ProbeSummary ssh={ssh} />

          {/*
            Поля тянут ширину, если им не запретить.

            У поля ввода и у списка есть своя минимальная ширина: у первого
            от атрибута `size`, у второго от самого длинного пункта —
            здесь это «диалог с сервером (keyboard-interactive)». Пока
            на них не стояло `w-full min-w-0`, колонка растягивалась под
            них, а вместе с ней панель узла: на телефоне это была
            горизонтальная прокрутка и обрезанные правым краем поля.
            Замерено на iPhone SE: 342 px содержимого в панели шириной 320.

            `minmax(0, 1fr)` на треке — вторая половина того же правила:
            трек с `1fr` тоже не сжимается ниже содержимого. Одного `w-full`
            на полях хватило, но держать оба честнее: следующее поле
            в этой сетке заведут, забыв про `min-w-0`.
          */}
          <div className="grid grid-cols-[86px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-sm">
            <label className="text-dim" htmlFor={`ssh-port-${node.id}`}>
              {t('agents.probe.port')}
            </label>
            {/*
              Ключ включает узел и сам порт — по той же причине, что и у VNC:
              поле неуправляемое (правку принимает потеря фокуса, иначе «2222»
              успело бы сохраниться как «2», «22» и «222»), а неуправляемое
              поле читает `defaultValue` один раз, при создании. Без ключа
              на соседнем узле в нём остался бы порт предыдущего.
            */}
            <input
              key={`${node.id}:${ssh.port}`}
              id={`ssh-port-${node.id}`}
              type="number"
              min={1}
              max={65535}
              defaultValue={ssh.port}
              disabled={!canWrite}
              onBlur={(event) => {
                const port = Number(event.target.value);

                if (port === ssh.port) {
                  return;
                }

                if (port >= 1 && port <= 65535) {
                  update.mutate({ port });
                  return;
                }

                event.target.value = String(ssh.port);
              }}
              className="h-8 w-full min-w-0 rounded-xs border border-line bg-bg px-2 font-mono text-sm"
            />

            <label className="text-dim" htmlFor={`ssh-auth-${node.id}`}>
              {t('ssh.login')}
            </label>
            <select
              id={`ssh-auth-${node.id}`}
              value={ssh.authMethod}
              disabled={!canWrite}
              onChange={(event) =>
                update.mutate({ authMethod: event.target.value as SshAuthMethod })
              }
              className="h-8 w-full min-w-0 rounded-xs border border-line bg-bg px-2 text-sm"
            >
              {SSH_AUTH_METHODS.map((method) => (
                <option key={method} value={method}>
                  {t(SSH_AUTH_METHOD_KEYS[method])}
                </option>
              ))}
            </select>

            <label className="text-dim" htmlFor={`ssh-term-${node.id}`}>
              TERM
            </label>
            <select
              id={`ssh-term-${node.id}`}
              value={ssh.term}
              disabled={!canWrite}
              onChange={(event) => update.mutate({ term: event.target.value as SshTerm })}
              className="h-8 w-full min-w-0 rounded-xs border border-line bg-bg px-2 text-sm"
            >
              {SSH_TERMS.map((term) => (
                <option key={term} value={term}>
                  {term}
                </option>
              ))}
            </select>

            <span className="text-dim">{t('ssh.algorithms')}</span>
            <label className="flex min-w-0 items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1 flex-none"
                checked={ssh.allowLegacyAlgorithms}
                disabled={!canWrite}
                onChange={(event) => update.mutate({ allowLegacyAlgorithms: event.target.checked })}
              />
              <span>
                {t('ssh.allowLegacy')}
                <span className="block text-xs text-dim">{t('ssh.allowLegacyHint')}</span>
              </span>
            </label>
          </div>

          {/*
            Имя пользователя здесь не спрашивается: оно часть учётных данных,
            рядом с паролем или ключом, которым этот пользователь входит.
            Пока поле было и тут, и там, одно значение лежало в двух местах —
            смотреть надо было в оба, а расходились они молча.
          */}
          {canManageCredentials ? (
            <NodeCredentials nodeId={node.id} service="ssh" emptyHint={NO_SSH_CREDENTIALS} />
          ) : null}

          <HostKey ssh={ssh} />

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              icon="refresh"
              disabled={probe.isPending}
              onClick={() => probe.mutate()}
            >
              {probe.isPending ? t('agents.probe.testing') : t('vnc.probeServer')}
            </Button>
            {probe.error ? (
              <span className="text-xs text-down">{errorText(probe.error)}</span>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-xs text-dim">{t('vnc.loadingSettings')}</p>
      )}
    </CollapsibleSection>
  );
}

/**
 * Закреплённый ключ хоста.
 *
 * Показывается всегда, когда он есть: сверять отпечаток с тем, что видно
 * на самой машине, — обычная работа администратора, и прятать его в отказ
 * значило бы показывать только тогда, когда уже поздно.
 */
function HostKey({ ssh }: { ssh: NodeSsh }): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  if (ssh.hostKeyFp === null) {
    return <p className="text-xs text-dim">{t('ssh.hostKeyNotPinned')}</p>;
  }

  return (
    <div className="text-xs">
      <span className="text-dim">
        {t('ssh.hostKeyLabel', { type: ssh.hostKeyType ?? t('ssh.hostKeyTypeUnknown') })}
      </span>
      <span className="break-all font-mono">{ssh.hostKeyFp}</span>
      {ssh.hostKeySeenAt === null ? null : (
        <span className="text-dim">
          {' '}
          · {t('ssh.hostKeyPinnedAt', { at: formatMoment(ssh.hostKeySeenAt, language) })}
        </span>
      )}
    </div>
  );
}

/**
 * Итог зондирования одной строкой.
 *
 * Разделены три состояния, потому что чинятся они по-разному: «не проверяли»,
 * «проверили и не достучались» и «проверили, всё нашли».
 */
function ProbeSummary({ ssh }: { ssh: NodeSsh }): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  if (ssh.lastProbeAt === null) {
    return <p className="text-xs text-dim">{t('vnc.notProbedYet')}</p>;
  }

  if (ssh.lastProbeError !== null) {
    return (
      <p className="text-xs text-down">
        {messageText(ssh.lastProbeError, t)}{' '}
        <span className="text-dim">({formatMoment(ssh.lastProbeAt, language)})</span>
      </p>
    );
  }

  return (
    <p className="text-xs text-dim">
      {ssh.banner ?? t('ssh.noBanner')}
      {ssh.authMethods === null || ssh.authMethods.length === 0
        ? ''
        : ` · ${t('ssh.accepts', {
            list: ssh.authMethods
              .map((method) => {
                // Сервер может назвать способ, о котором платформа не знает:
                // список приходит от него, а не из нашего перечисления.
                const key: string | undefined = SSH_AUTH_METHOD_KEYS[method as SshAuthMethod];

                return key === undefined ? method : t(key);
              })
              .join(', '),
          })}`}
      {ssh.sftpSupported === false ? ` · ${t('ssh.noSftp')}` : ''}
      <span className="text-dim">
        {' '}
        · {t('vnc.probedAt', { at: formatMoment(ssh.lastProbeAt, language) })}
      </span>
    </p>
  );
}
